import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { ExternalLink, Zap as ZapIcon, Link as LinkIcon } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface ProfileCardProps {
  pubkey: string;
  npub: string;
}

export function ProfileCard({ pubkey, npub }: ProfileCardProps) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;

  const displayName = metadata?.name || genUserName(pubkey);
  const avatarUrl = metadata?.picture;
  const bannerUrl = metadata?.banner;

  if (author.isLoading) {
    return (
      <Card className="overflow-hidden">
        <Skeleton className="h-32 w-full" />
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <Skeleton className="h-20 w-20 rounded-full -mt-16 ring-4 ring-background" />
            <div className="flex-1 space-y-2 pt-2">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-4 w-48" />
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      {bannerUrl && (
        <div className="h-32 overflow-hidden">
          <img
            src={bannerUrl}
            alt="Banner"
            className="w-full h-full object-cover"
          />
        </div>
      )}
      {!bannerUrl && (
        <div className="h-32 bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20" />
      )}
      
      <CardContent className="pt-6">
        <div className="flex flex-col sm:flex-row items-start gap-4">
          <Avatar className="h-20 w-20 -mt-16 ring-4 ring-background">
            <AvatarImage src={avatarUrl} alt={displayName} />
            <AvatarFallback className="text-2xl">
              {displayName[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0 space-y-3">
            <div>
              <h2 className="font-heading text-2xl font-bold">{displayName}</h2>
              {metadata?.nip05 && (
                <p className="text-sm text-muted-foreground mt-1">
                  ✓ {metadata.nip05}
                </p>
              )}
            </div>

            {metadata?.about && (
              <p className="text-sm leading-relaxed whitespace-pre-wrap">
                {metadata.about}
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              {metadata?.website && (
                <Button asChild variant="outline" size="sm">
                  <a
                    href={metadata.website}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <LinkIcon className="h-3 w-3 mr-2" />
                    Website
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                </Button>
              )}
              {(metadata?.lud16 || metadata?.lud06) && (
                <Badge variant="secondary" className="px-3 py-1">
                  <ZapIcon className="h-3 w-3 mr-1.5 text-yellow-600 dark:text-yellow-500" />
                  {metadata.lud16 || metadata.lud06}
                </Badge>
              )}
            </div>

            <div className="pt-2">
              <p className="text-xs text-muted-foreground font-mono break-all">
                {npub}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
